import React, { useState, useRef, useCallback } from 'react';
import { css } from '@emotion/css';
import { v4 as uuidv4 } from 'uuid';
import * as voicifyApi from '../../../api';
import MultiFileSelector from '../../../components/forms/MultiFileSelector';
import SettingsCard from '../../../components/structure/SettingsCard';
import {
    color_shades_darkest,
    color_shades_darker,
    color_shades_light,
    color_shades_lighter,
    color_defaults_white,
    color_variants_main_bg,
    greeny_blue,
    faded_red,
    dark_sky_blue,
    cool_grey,
} from '../../../constants/colors';
import {
    getAIAnsweringModuleId,
    getFoodOrderingModuleId,
    getHealthcareSchedulingModuleId,
} from '../../../constants/ModuleConsts';
import {
    aiAnsweringCustomModuleName,
    aiOrderingCustomModuleName,
    aiDentalSchedulingCustomModuleName,
    aiAnsweringCommonAppName,
    aiOrderingCommonAppName,
    aiDentalSchedulingCommonAppName,
} from '../../../constants/sparkConstants/moduleConsts';
import TemplateConfigurationModel from '../../../models/templating/api/TemplateConfigurationModel';
import AppliedApplicationTemplateModel from '../../../models/templating/api/AppliedApplicationTemplateModel';
import TemplateFormModel from '../../../models/templating/api/TemplateFormModel';
import TemplateFormSectionModel from '../../../models/templating/api/TemplateFormSectionModel';
import TemplateFormFieldModel from '../../../models/templating/api/TemplateFormFieldModel';
import OrganizationModel from '../../../models/organizations/api/OrganizationModel';
import ApplicationModel from '../../../models/applications/api/ApplicationModel';
import { csvTextToArray, convertCSVToTemplateSections, sectionsToTemplateForm, ImportedTemplateFormCsvSection, exportCSV } from '../../../utils/templateFormCsv';
import {
    buildStrictCsvFieldSyncPayload,
    buildStrictCsvSectionCreationPayload,
    StrictCsvSyncNewField,
    buildStrictCsvSyncPlan,
    StrictCsvSyncFieldChange,
    StrictCsvSyncPlan,
} from '../../../utils/templateFormCsvStrictSync';
import TemplateFormFieldOptionModel from '../../../models/templating/api/TemplateFormFieldOptionModel';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type AssistantType = 'ai-answering' | 'ai-ordering' | 'dental-scheduling';
type ExportAssistantType = 'ai-ordering' | 'dental-scheduling';
type ExportApplicationType = 'common' | 'location';
type TemplateSourceType = 'core' | 'org';

type ItemStatus =
    | 'pending'
    | 'running'
    | 'updated'
    | 'skipped'
    | 'error'
    | 'removed_fields'
    | 'new_fields';

interface MigrationItem {
    key: string;
    label: string;
    status: ItemStatus;
    removedFields?: string[];
    newFields?: string[];
    error?: string;
}

type MigrationStatus = 'idle' | 'running' | 'paused' | 'done' | 'error';

type UploadSourceType = 'json' | 'csv';

type LogLevel = 'info' | 'change' | 'add' | 'remove' | 'skip' | 'warn';

interface DryRunLog {
    itemKey: string;
    level: LogLevel;
    message: string;
}

interface UploadedMigrationTemplate {
    sourceType: UploadSourceType;
    displayName: string;
    templateForm: TemplateFormModel;
    csvSections?: ImportedTemplateFormCsvSection[];
}

interface ModuleExportBundle {
    exportedAt: string;
    environment: string;
    core: Record<ExportAssistantType, TemplateConfigurationModel | null>;
    organizations: Array<{
        organizationId: string;
        organizationName: string;
        modules: Record<ExportAssistantType, TemplateConfigurationModel | null>;
    }>;
}

interface AppliedModuleExportMatch {
    assistantType: ExportAssistantType;
    templateConfigurationId: string;
    templateConfigurationName: string;
    templateApplicationId: string;
    templateSource: TemplateSourceType;
}

const APPLIED_MODULE_CSV_HEADERS = [
    'organizationId',
    'organizationName',
    'assistantType',
    'applicationId',
    'applicationName',
    'applicationType',
    'allowsTemplating',
    'manageMenuApplicationId',
    'posName',
    'posLocationId',
    'pmsName',
    'pmsLocationId',
    'moduleName',
    'appliedTemplateId',
    'appliedApplicationId',
    'templateConfigurationId',
    'templateConfigurationName',
    'templateApplicationId',
    'templateSource',
    'sectionId',
    'sectionTitle',
    'sectionPriority',
    'fieldId',
    'fieldTitle',
    'fieldLabel',
    'fieldType',
    'associatedVariable',
    'placeholder',
    'tip',
    'defaultValue',
    'currentValue',
    'conditionalDefaults',
    'options',
    'isRequired',
    'priority'
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getModuleId(assistantType: AssistantType): string | null {
    switch (assistantType) {
        case 'ai-answering':      return getAIAnsweringModuleId();
        case 'ai-ordering':       return getFoodOrderingModuleId();
        case 'dental-scheduling': return getHealthcareSchedulingModuleId();
    }
}

function getCustomModuleName(assistantType: AssistantType): string {
    switch (assistantType) {
        case 'ai-answering':      return aiAnsweringCustomModuleName;
        case 'ai-ordering':       return aiOrderingCustomModuleName;
        case 'dental-scheduling': return aiDentalSchedulingCustomModuleName;
    }
}

function getCommonAppName(assistantType: AssistantType): string {
    switch (assistantType) {
        case 'ai-answering':      return aiAnsweringCommonAppName;
        case 'ai-ordering':       return aiOrderingCommonAppName;
        case 'dental-scheduling': return aiDentalSchedulingCommonAppName;
    }
}

const exportAssistantTypes: ExportAssistantType[] = ['ai-ordering', 'dental-scheduling'];

function downloadJsonFile(fileName: string, data: unknown) {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
}

function getOrgSpecificModuleConfig(orgConfigs: TemplateConfigurationModel[], assistantType: ExportAssistantType): TemplateConfigurationModel | null {
    const customModuleName = getCustomModuleName(assistantType);

    if (orgConfigs.length === 1) {
        return orgConfigs[0];
    }

    return orgConfigs.find(config => config.name === customModuleName) ?? null;
}

function isManageMenuApp(application: ApplicationModel, manageMenuAppIds: Set<string>): boolean {
    const normalizedName = application.name?.toLowerCase() ?? '';

    return !!application.id && (
        manageMenuAppIds.has(application.id) ||
        (normalizedName.startsWith('x -') && normalizedName.endsWith('menu')) ||
        normalizedName.includes('manage menu') ||
        normalizedName.includes('manage entities')
    );
}

async function mapWithConcurrency<T, TResult>(
    items: T[],
    concurrency: number,
    mapper: (item: T, index: number) => Promise<TResult>
): Promise<TResult[]> {
    const results: TResult[] = new Array(items.length);
    const workerCount = Math.max(1, Math.min(concurrency, items.length));
    let nextIndex = 0;

    await Promise.all(Array.from({ length: workerCount }, async () => {
        while (nextIndex < items.length) {
            const currentIndex = nextIndex;
            nextIndex += 1;
            results[currentIndex] = await mapper(items[currentIndex], currentIndex);
        }
    }));

    return results;
}

function buildAppliedModuleCsvRows(
    organization: OrganizationModel,
    application: ApplicationModel,
    applicationType: ExportApplicationType,
    matchedModule: AppliedModuleExportMatch,
    appliedTemplate: AppliedApplicationTemplateModel
): Array<Record<string, string | number>> {
    const templateFields = (appliedTemplate.templateForm?.templateFormSections ?? []).flatMap(section => {
        return (section.templateFormFields ?? []).map(field => ({
            section,
            field,
        }));
    });

    const templateFieldLookup = templateFields.reduce((accumulator, entry) => {
        if (entry.field.id) {
            accumulator[entry.field.id] = entry;
        }
        return accumulator;
    }, {} as Record<string, { section: TemplateFormSectionModel; field: TemplateFormFieldModel }>);

    const fieldValueLookup = (appliedTemplate.templateFormFieldValues ?? []).reduce((accumulator, fieldValue) => {
        if (fieldValue.templateFormFieldId) {
            accumulator[fieldValue.templateFormFieldId] = fieldValue.value ?? '';
        }
        return accumulator;
    }, {} as Record<string, string>);

    const templateRows = templateFields.map(({ section, field }) => ({
        organizationId: organization.id,
        organizationName: organization.name,
        assistantType: matchedModule.assistantType,
        applicationId: application.id ?? '',
        applicationName: application.name ?? '',
        applicationType,
        allowsTemplating: application.allowsTemplating ? 'true' : 'false',
        manageMenuApplicationId: application.manageMenuApplicationId ?? '',
        posName: application.posName ?? '',
        posLocationId: application.posLocationId ?? '',
        pmsName: application.pmsName ?? '',
        pmsLocationId: application.pmsLocationId ?? '',
        moduleName: appliedTemplate.name ?? '',
        appliedTemplateId: appliedTemplate.id,
        appliedApplicationId: appliedTemplate.appliedApplicationId,
        templateConfigurationId: matchedModule.templateConfigurationId,
        templateConfigurationName: matchedModule.templateConfigurationName,
        templateApplicationId: matchedModule.templateApplicationId,
        templateSource: matchedModule.templateSource,
        sectionId: section.id ?? '',
        sectionTitle: section.title ?? '',
        sectionPriority: section.priority ?? '',
        fieldId: field.id ?? '',
        fieldTitle: field.title ?? '',
        fieldLabel: field.label ?? '',
        fieldType: field.fieldType ?? '',
        associatedVariable: field.associatedVariable ?? '',
        placeholder: field.placeholder ?? '',
        tip: field.tip ?? '',
        defaultValue: field.defaultValue ?? '',
        currentValue: field.id ? fieldValueLookup[field.id] ?? '' : '',
        conditionalDefaults: field.conditionalDefaults ? JSON.stringify(field.conditionalDefaults) : '',
        options: field.options ? JSON.stringify(field.options) : '',
        isRequired: field.isRequired ? 'true' : 'false',
        priority: field.priority ?? '',
    }));

    const valueOnlyRows = (appliedTemplate.templateFormFieldValues ?? []).reduce((accumulator, fieldValue) => {
        if (fieldValue.templateFormFieldId && templateFieldLookup[fieldValue.templateFormFieldId]) {
            return accumulator;
        }

        const fallbackFieldId = fieldValue.templateFormFieldId ?? fieldValue.dynamicFormFieldId ?? '';
        const fallbackTitle = fieldValue.title ?? fieldValue.label ?? fieldValue.associatedVariable ?? fallbackFieldId;
        const hasUsefulData = !!(
            fallbackFieldId ||
            fallbackTitle ||
            fieldValue.value ||
            fieldValue.associatedVariable ||
            fieldValue.dynamicFormFieldName
        );

        if (!hasUsefulData) {
            return accumulator;
        }

        accumulator.push({
            organizationId: organization.id,
            organizationName: organization.name,
            assistantType: matchedModule.assistantType,
            applicationId: application.id ?? '',
            applicationName: application.name ?? '',
            applicationType,
            allowsTemplating: application.allowsTemplating ? 'true' : 'false',
            manageMenuApplicationId: application.manageMenuApplicationId ?? '',
            posName: application.posName ?? '',
            posLocationId: application.posLocationId ?? '',
            pmsName: application.pmsName ?? '',
            pmsLocationId: application.pmsLocationId ?? '',
            moduleName: appliedTemplate.name ?? '',
            appliedTemplateId: appliedTemplate.id,
            appliedApplicationId: appliedTemplate.appliedApplicationId,
            templateConfigurationId: matchedModule.templateConfigurationId,
            templateConfigurationName: matchedModule.templateConfigurationName,
            templateApplicationId: matchedModule.templateApplicationId,
            templateSource: matchedModule.templateSource,
            sectionId: '',
            sectionTitle: '',
            sectionPriority: '',
            fieldId: fallbackFieldId,
            fieldTitle: fallbackTitle,
            fieldLabel: fieldValue.label ?? '',
            fieldType: fieldValue.fieldType ?? '',
            associatedVariable: fieldValue.associatedVariable ?? fieldValue.dynamicFormFieldName ?? '',
            placeholder: fieldValue.placeholder ?? '',
            tip: fieldValue.tip ?? '',
            defaultValue: fieldValue.defaultValue ?? '',
            currentValue: fieldValue.value ?? '',
            conditionalDefaults: '',
            options: '',
            isRequired: 'false',
            priority: '',
        });

        return accumulator;
    }, [] as Array<Record<string, string | number>>);

    return [...templateRows, ...valueOnlyRows];
}

// ---------------------------------------------------------------------------
// Core merge logic
// ---------------------------------------------------------------------------

interface MergeResult {
    merged: TemplateFormModel;
    removedFields: TemplateFormFieldModel[];
}

const JSON_SECTION_COMPARE_PROPS: (keyof TemplateFormSectionModel)[] = ['title', 'instructionsMarkdown', 'priority', 'isCustom', 'name'];

const JSON_FIELD_COMPARE_PROPS: (keyof TemplateFormFieldModel)[] = [
    'label', 'fieldType', 'placeholder', 'defaultValue', 'tip',
    'title', 'isRequired', 'instructionsMarkdown', 'priority',
    'suggestions', 'fileFormats', 'recentlyAdded', 'name', 'associatedVariable',
];

const normalizeFieldOptions = (
    options: TemplateFormFieldOptionModel[] | undefined,
    templateFormFieldId: string | undefined,
    regenerateOptionIds: boolean = false,
): TemplateFormFieldOptionModel[] => {
    return (options ?? []).map(option => ({
        ...option,
        id: regenerateOptionIds || !option.id ? uuidv4() : option.id,
        templateFormFieldId,
    }));
};

function validateTemplateConfigurationPayload(template: TemplateConfigurationModel): string[] {
    const errors: string[] = [];
    const templateForm = template.templateForm;

    if (!template.id) errors.push('Template configuration is missing an id.');
    if (!templateForm?.id) errors.push('Template form is missing an id.');

    const seenSectionIds = new Set<string>();
    const seenFieldIds = new Set<string>();
    const seenOptionIds = new Set<string>();
    const sectionIdsInPayload = new Set<string>();

    (templateForm?.templateFormSections ?? []).forEach(section => {
        if (section.id) {
            sectionIdsInPayload.add(section.id);
        }
    });

    for (const [sectionIndex, section] of (templateForm?.templateFormSections ?? []).entries()) {
        if (section.id && seenSectionIds.has(section.id)) {
            errors.push(`Duplicate section id detected: ${section.id}.`);
        } else if (section.id) {
            seenSectionIds.add(section.id);
        }

        if (section.templateFormId && templateForm?.id && section.templateFormId !== templateForm.id) {
            errors.push(`Section "${section.title ?? sectionIndex + 1}" has templateFormId ${section.templateFormId} but expected ${templateForm.id}.`);
        }

        for (const [fieldIndex, field] of (section.templateFormFields ?? []).entries()) {
            if (field.id && seenFieldIds.has(field.id)) {
                errors.push(`Duplicate field id detected: ${field.id}.`);
            } else if (field.id) {
                seenFieldIds.add(field.id);
            }

            if (field.templateFormSectionId && !sectionIdsInPayload.has(field.templateFormSectionId)) {
                errors.push(`Field "${field.title ?? fieldIndex + 1}" in section "${section.title ?? sectionIndex + 1}" points to missing section id ${field.templateFormSectionId}.`);
            } else if (field.templateFormSectionId && section.id && field.templateFormSectionId !== section.id) {
                errors.push(`Field "${field.title ?? fieldIndex + 1}" in section "${section.title ?? sectionIndex + 1}" has templateFormSectionId ${field.templateFormSectionId} but expected ${section.id}.`);
            }

            for (const option of field.options ?? []) {
                if (option.id && seenOptionIds.has(option.id)) {
                    errors.push(`Duplicate option id detected: ${option.id}.`);
                } else if (option.id) {
                    seenOptionIds.add(option.id);
                }

                if (option.templateFormFieldId && field.id && option.templateFormFieldId !== field.id) {
                    errors.push(`Option "${option.label ?? 'unknown'}" in field "${field.title ?? fieldIndex + 1}" has templateFormFieldId ${option.templateFormFieldId} but expected ${field.id}.`);
                }
            }
        }
    }

    return errors;
}

function validateSourceTemplateConfiguration(template: TemplateConfigurationModel): string[] {
    const errors: string[] = [];
    const sections = template.templateForm?.templateFormSections ?? [];
    const seenSectionTitles = new Set<string>();
    const seenFieldIds = new Set<string>();
    const seenAssociatedVariables = new Set<string>();

    for (const [sectionIndex, section] of sections.entries()) {
        if (section.title) {
            if (seenSectionTitles.has(section.title)) {
                errors.push(`Duplicate section title detected in source template: "${section.title}".`);
            }
            seenSectionTitles.add(section.title);
        }

        if (!section.id) {
            errors.push(`Source section ${sectionIndex + 1} is missing an id.`);
        }

        for (const [fieldIndex, field] of (section.templateFormFields ?? []).entries()) {
            if (field.id) {
                if (seenFieldIds.has(field.id)) {
                    errors.push(`Duplicate field id detected in source template: ${field.id}.`);
                }
                seenFieldIds.add(field.id);
            }

            if (field.templateFormSectionId && section.id && field.templateFormSectionId !== section.id) {
                errors.push(`Source field "${field.title ?? fieldIndex + 1}" is nested under section "${section.title ?? sectionIndex + 1}" but points at templateFormSectionId ${field.templateFormSectionId}.`);
            }

            if (field.associatedVariable) {
                if (seenAssociatedVariables.has(field.associatedVariable)) {
                    errors.push(`Duplicate associatedVariable detected in source template: ${field.associatedVariable}.`);
                }
                seenAssociatedVariables.add(field.associatedVariable);
            }
        }
    }

    return errors;
}

function collectTemplateIdentitySets(template: TemplateConfigurationModel) {
    const sectionIds = new Set<string>();
    const fieldIds = new Set<string>();
    const optionIds = new Set<string>();

    (template.templateForm?.templateFormSections ?? []).forEach(section => {
        if (section.id) sectionIds.add(section.id);
        (section.templateFormFields ?? []).forEach(field => {
            if (field.id) fieldIds.add(field.id);
            (field.options ?? []).forEach(option => {
                if (option.id) optionIds.add(option.id);
            });
        });
    });

    return { sectionIds, fieldIds, optionIds };
}

function normalizeTemplateConfigurationPayload(template: TemplateConfigurationModel, sourceTemplate: TemplateConfigurationModel): TemplateConfigurationModel {
    if (!template?.templateForm) {
        return template;
    }

    const sourceIds = collectTemplateIdentitySets(sourceTemplate);

    const normalizedTemplateFormSections = (template.templateForm.templateFormSections ?? []).map(section => {
        const normalizedSection: any = { ...section };
        const sectionIsExisting = !!(section.id && sourceIds.sectionIds.has(section.id));

        if (!sectionIsExisting) {
            delete normalizedSection.id;
            delete normalizedSection.templateFormId;
        }

        normalizedSection.templateFormFields = (section.templateFormFields ?? []).map(field => {
            const normalizedField: any = { ...field };
            const fieldIsExisting = !!(field.id && sourceIds.fieldIds.has(field.id));

            if (!fieldIsExisting) {
                delete normalizedField.id;
            }

            if (sectionIsExisting && fieldIsExisting) {
                normalizedField.templateFormSectionId = section.id;
            } else {
                delete normalizedField.templateFormSectionId;
            }

            normalizedField.options = (field.options ?? []).map(option => {
                const normalizedOption: any = { ...option };
                const optionIsExisting = !!(option.id && sourceIds.optionIds.has(option.id));

                if (!optionIsExisting) {
                    delete normalizedOption.id;
                }

                if (fieldIsExisting) {
                    normalizedOption.templateFormFieldId = field.id;
                } else {
                    delete normalizedOption.templateFormFieldId;
                }

                return normalizedOption;
            });

            return normalizedField;
        });

        return normalizedSection;
    });

    return {
        ...template,
        templateForm: {
            ...template.templateForm,
            templateFormSections: normalizedTemplateFormSections,
        },
    };
}

/**
 * Merges the incoming (new) templateForm layout into the existing one.
 * CSV imports preserve any existing properties not represented in the export.
 *
 * Section matching:  by title
 * Field matching:    primary = associatedVariable (when set), fallback = title
 *
 * On a matched field:
 *   - Retain existing field.id and templateFormSectionId
 *   - Overwrite everything else (including conditionalDefaults) from incoming
 *
 * New sections/fields (in incoming only) → appended with fresh UUIDs
 * Removed fields (in existing only)      → collected in removedFields; caller decides
 * Empty sections after merge             → removed from output
 */
function mergeTemplateForm(existing: TemplateFormModel, incoming: TemplateFormModel): MergeResult {
    const removedFields: TemplateFormFieldModel[] = [];
    const existingFormId = existing.id;

    // Build a map of existing sections keyed by title for fast lookup
    const existingSectionsByTitle = new Map<string, TemplateFormSectionModel>();
    for (const section of existing.templateFormSections ?? []) {
        if (section.title) {
            existingSectionsByTitle.set(section.title, section);
        }
    }

    // Track which existing sections we touched (for leftover detection)
    const touchedSectionTitles = new Set<string>();

    const mergedSections: TemplateFormSectionModel[] = [];

    // 1. Walk every section in the incoming layout
    for (const incomingSection of incoming.templateFormSections ?? []) {
        const existingSection = incomingSection.title
            ? existingSectionsByTitle.get(incomingSection.title)
            : undefined;

        if (existingSection) {
            // Matched section — merge fields
            touchedSectionTitles.add(incomingSection.title);

            const { mergedFields, removed } = mergeFields(
                existingSection.templateFormFields ?? [],
                incomingSection.templateFormFields ?? [],
                existingSection.id,
            );

            removedFields.push(...removed);

            // Only include the section if it has fields after the merge
            if (mergedFields.length > 0) {
                mergedSections.push({
                    ...existingSection,
                    templateFormId: existingSection.templateFormId ?? existingFormId,
                    title: incomingSection.title,
                    instructionsMarkdown: incomingSection.instructionsMarkdown,
                    priority: incomingSection.priority,
                    isCustom: incomingSection.isCustom,
                    templateFormFields: mergedFields,
                });
            }
        } else {
            // New section — assign fresh UUIDs to section and all its fields
            const newSectionId = uuidv4();
            const newFields: TemplateFormFieldModel[] = (incomingSection.templateFormFields ?? []).map(f => {
                const newFieldId = uuidv4();
                return {
                    ...f,
                    id: newFieldId,
                    templateFormSectionId: newSectionId,
                    options: normalizeFieldOptions(f.options, newFieldId, true),
                };
            });

            if (newFields.length > 0) {
                mergedSections.push({
                    ...incomingSection,
                    id: newSectionId,
                    templateFormId: existingFormId,
                    templateFormFields: newFields,
                });
            }
        }
    }

    // 2. Detect any existing sections that were NOT touched by the incoming layout.
    //    Their fields are "removed" from the new layout perspective — surface them
    //    via removedFields so the caller can pause and warn. These sections are NOT
    //    included in the merged output — if the user continues, the fields are dropped.
    for (const existingSection of existing.templateFormSections ?? []) {
        if (existingSection.title && !touchedSectionTitles.has(existingSection.title)) {
            const orphanedFields = existingSection.templateFormFields ?? [];
            if (orphanedFields.length > 0) {
                removedFields.push(...orphanedFields);
            }
        }
    }

    const merged: TemplateFormModel = {
        ...existing,
        templateFormSections: mergedSections,
    };

    return { merged, removedFields };
}

interface FieldMergeResult {
    mergedFields: TemplateFormFieldModel[];
    removed: TemplateFormFieldModel[];
}

function mergeFields(
    existingFields: TemplateFormFieldModel[],
    incomingFields: TemplateFormFieldModel[],
    sectionId: string,
): FieldMergeResult {
    const removed: TemplateFormFieldModel[] = [];

    // Index existing fields by associatedVariable and by title for fast lookup
    const existingByAssocVar = new Map<string, TemplateFormFieldModel>();
    const existingByTitle    = new Map<string, TemplateFormFieldModel>();
    for (const f of existingFields) {
        if (f.associatedVariable) existingByAssocVar.set(f.associatedVariable, f);
        if (f.title)              existingByTitle.set(f.title, f);
    }

    const matchedExistingIds = new Set<string>();
    const mergedFields: TemplateFormFieldModel[] = [];

    for (const incomingField of incomingFields) {
        // Primary match: associatedVariable; fallback: title
        const existingField =
            (incomingField.associatedVariable && existingByAssocVar.get(incomingField.associatedVariable)) ||
            (incomingField.title              && existingByTitle.get(incomingField.title))                 ||
            null;

        if (existingField) {
            matchedExistingIds.add(existingField.id);
            const mergedField: TemplateFormFieldModel = {
                ...incomingField,
                id: existingField.id,
                templateFormSectionId: sectionId,
            };
            mergedField.options = normalizeFieldOptions(incomingField.options ?? existingField.options, mergedField.id, false);
            mergedFields.push(mergedField);
        } else {
            const newFieldId = uuidv4();
            const newField: TemplateFormFieldModel = {
                ...incomingField,
                id: newFieldId,
                templateFormSectionId: sectionId,
            };
            newField.options = normalizeFieldOptions(incomingField.options, newFieldId, true);
            mergedFields.push(newField);
        }
    }

    // Detect fields that exist in old layout but not in new → removed
    for (const existingField of existingFields) {
        if (!matchedExistingIds.has(existingField.id)) {
            removed.push(existingField);
        }
    }

    return { mergedFields, removed };
}

// ---------------------------------------------------------------------------
// Dry-run change logger
// ---------------------------------------------------------------------------

/**
 * Compares the existing and incoming templateForms and emits human-readable
 * log entries describing what the merge would change.
 */
function logMergeChanges(
    itemKey: string,
    existing: TemplateFormModel,
    incoming: TemplateFormModel,
    merged: TemplateFormModel,
    addLog: (key: string, level: LogLevel, msg: string) => void,
) {
    // Track whether any substantive log was emitted for this item
    let hasChanges = false;
    const trackingAddLog = (key: string, level: LogLevel, msg: string) => {
        if (level !== 'info' && level !== 'skip') hasChanges = true;
        addLog(key, level, msg);
    };
    const existingSections = existing.templateFormSections ?? [];
    const incomingSections = incoming.templateFormSections ?? [];
    const mergedSections   = merged.templateFormSections ?? [];

    const sectionCompareProps = JSON_SECTION_COMPARE_PROPS;
    const fieldCompareProps = JSON_FIELD_COMPARE_PROPS;

    const existingSectionTitles = new Set(existingSections.map(s => s.title));
    const incomingSectionTitles = new Set(incomingSections.map(s => s.title));

    // New sections
    for (const s of incomingSections) {
        if (s.title && !existingSectionTitles.has(s.title)) {
            trackingAddLog(itemKey, 'add', `Section added: "${s.title}" (${s.templateFormFields?.length ?? 0} fields)`);
        }
    }

    // Removed sections (existing not in incoming — will be dropped from merged output)
    for (const s of existingSections) {
        if (s.title && !incomingSectionTitles.has(s.title)) {
            trackingAddLog(itemKey, 'remove', `Section not in new layout: "${s.title}" — fields will be removed`);
        }
    }

    // Field-level changes within matched sections
    for (const incomingSection of incomingSections) {
        if (!incomingSection.title) continue;
        const existingSection = existingSections.find(s => s.title === incomingSection.title);
        if (!existingSection) continue; // new section already logged above

        const mergedSection = mergedSections.find(s => s.title === incomingSection.title);
        if (!mergedSection) continue;

        const sectionChanged: string[] = [];
        for (const prop of sectionCompareProps) {
            const oldVal = JSON.stringify(existingSection[prop] ?? null);
            const newVal = JSON.stringify(mergedSection[prop] ?? null);
            if (oldVal !== newVal) {
                sectionChanged.push(`${prop}: ${oldVal} → ${newVal}`);
            }
        }

        if (sectionChanged.length > 0) {
            trackingAddLog(itemKey, 'change', `Section updated: "${incomingSection.title}" — ${sectionChanged.join('; ')}`);
        }

        const existingFields  = existingSection.templateFormFields  ?? [];
        const incomingFields  = incomingSection.templateFormFields  ?? [];
        const mergedFields    = mergedSection?.templateFormFields   ?? [];

        const existingByAssocVar = new Map(existingFields.filter(f => f.associatedVariable).map(f => [f.associatedVariable, f]));
        const existingByTitle    = new Map(existingFields.filter(f => f.title).map(f => [f.title, f]));

        for (const incomingField of incomingFields) {
            const existingField =
                (incomingField.associatedVariable && existingByAssocVar.get(incomingField.associatedVariable)) ||
                (incomingField.title && existingByTitle.get(incomingField.title)) ||
                null;

            const fieldLabel = incomingField.title ?? incomingField.associatedVariable ?? incomingField.id;

            if (!existingField) {
                trackingAddLog(itemKey, 'add', `  [${incomingSection.title}] Field added: "${fieldLabel}"`);
                continue;
            }

            // Find the merged version (same id as existing)
            const mergedField = mergedFields.find(f => f.id === existingField.id);
            if (!mergedField) continue;

            // Compare every overwriteable property
            const changed: string[] = [];
            for (const prop of fieldCompareProps) {
                const oldVal = JSON.stringify(existingField[prop] ?? null);
                const newVal = JSON.stringify(mergedField[prop] ?? null);
                if (oldVal !== newVal) {
                    changed.push(`${prop}: ${oldVal} → ${newVal}`);
                }
            }

            // Check options array
            const oldOptions = JSON.stringify(existingField.options ?? []);
            const newOptions = JSON.stringify(mergedField.options ?? []);
            if (oldOptions !== newOptions) changed.push('options changed');

            // Check conditionalDefaults
            const oldCond = JSON.stringify(existingField.conditionalDefaults ?? null);
            const newCond = JSON.stringify(mergedField.conditionalDefaults ?? null);
            if (oldCond !== newCond) changed.push('conditionalDefaults changed');

            if (changed.length > 0) {
                trackingAddLog(itemKey, 'change', `  [${incomingSection.title}] Field updated: "${fieldLabel}" — ${changed.join('; ')}`);
            }
        }

        // Fields in existing section not matched by incoming
        const matchedExistingIds = new Set(
            incomingFields.map(inf => {
                const ef =
                    (inf.associatedVariable && existingByAssocVar.get(inf.associatedVariable)) ||
                    (inf.title && existingByTitle.get(inf.title)) ||
                    null;
                return ef?.id;
            }).filter(Boolean)
        );
        for (const ef of existingFields) {
            if (!matchedExistingIds.has(ef.id)) {
                const fieldLabel = ef.title ?? ef.associatedVariable ?? ef.id;
                trackingAddLog(itemKey, 'remove', `  [${incomingSection.title}] Field removed: "${fieldLabel}"`);
            }
        }
    }

    if (!hasChanges) {
        addLog(itemKey, 'info', 'No structural changes detected.');
    }
}

function logStrictCsvFieldChanges(
    itemKey: string,
    fieldChanges: StrictCsvSyncFieldChange[],
    addLog: (key: string, level: LogLevel, msg: string) => void,
) {
    const meaningfulChanges = fieldChanges.filter(change => change.changedProps.length > 0);

    if (meaningfulChanges.length === 0) {
        addLog(itemKey, 'info', 'No field setting changes detected.');
        return;
    }

    meaningfulChanges.forEach(change => {
        const moveMessage = change.fromSectionTitle !== change.toSectionTitle
            ? ` moved ${change.fromSectionTitle ?? 'unknown'} -> ${change.toSectionTitle};`
            : '';
        addLog(
            itemKey,
            'change',
            `[${change.associatedVariable}] ${change.title}${moveMessage} updated ${change.changedProps.join(', ')}`,
        );
    });
}

function formatNewFieldLabel(field: StrictCsvSyncNewField): string {
    return `${field.title} [${field.associatedVariable}] -> ${field.sectionTitle} (${field.fieldType})`;
}

async function runStrictCsvSyncForTemplate(args: {
    itemKey: string;
    template: TemplateConfigurationModel;
    plan: StrictCsvSyncPlan;
    allowNewFields: boolean;
    isDryRun: boolean;
    addLog: (key: string, level: LogLevel, msg: string) => void;
    confirmNewFields?: (fields: StrictCsvSyncNewField[]) => Promise<boolean>;
}): Promise<{ ok: boolean; error?: string }> {
    const { itemKey, template, plan, allowNewFields, isDryRun, addLog, confirmNewFields } = args;

    const sectionCreation = buildStrictCsvSectionCreationPayload(template, plan, allowNewFields);
    if (sectionCreation.errors.length > 0) {
        return { ok: false, error: sectionCreation.errors.join(' ') };
    }

    let workingTemplate = template;

    if (sectionCreation.missingSections.length > 0) {
        const sectionTitles = sectionCreation.missingSections.map(section => section.title).join(', ');
        addLog(itemKey, 'add', `Create missing sections: ${sectionTitles}`);

        if (!isDryRun) {
            const createSectionsResult = await voicifyApi.updateTemplateConfiguration(template.id, sectionCreation.templateConfiguration);
            if (createSectionsResult.resultType !== 'Ok') {
                return { ok: false, error: 'PUT failed while creating sections: ' + (createSectionsResult.errors?.join(', ') ?? 'unknown') };
            }

            workingTemplate = createSectionsResult.data;
        } else {
            workingTemplate = sectionCreation.templateConfiguration;
        }
    }

    const fieldSync = buildStrictCsvFieldSyncPayload(workingTemplate, plan, allowNewFields, isDryRun);
    if (fieldSync.errors.length > 0) {
        return { ok: false, error: fieldSync.errors.join(' ') };
    }

    if (fieldSync.newFields.length > 0) {
        fieldSync.newFields.forEach(field => addLog(itemKey, 'add', `New core field: ${formatNewFieldLabel(field)}`));

        if (confirmNewFields) {
            const shouldContinue = await confirmNewFields(fieldSync.newFields);
            if (!shouldContinue) {
                return { ok: false, error: 'Migration stopped because new core fields were not confirmed.' };
            }
        }
    }

    logStrictCsvFieldChanges(itemKey, fieldSync.fieldChanges, addLog);

    if (isDryRun) {
        addLog(itemKey, 'info', '[DRY RUN] Strict CSV field sync skipped — no changes saved.');
        return { ok: true };
    }

    const updateResult = await voicifyApi.updateTemplateConfiguration(template.id, fieldSync.templateConfiguration);
    if (updateResult.resultType !== 'Ok') {
        return { ok: false, error: 'PUT failed while moving/updating fields: ' + (updateResult.errors?.join(', ') ?? 'unknown') };
    }

    return { ok: true };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const MigrationRunner: React.FC = () => {
    const [uploadedTemplate, setUploadedTemplate] = useState<UploadedMigrationTemplate | null>(null);
    const [uploadError, setUploadError]       = useState<string | null>(null);
    const [assistantType, setAssistantType]   = useState<AssistantType>('ai-answering');
    const [isDryRun, setIsDryRun]             = useState<boolean>(true);
    const [migrationStatus, setMigrationStatus] = useState<MigrationStatus>('idle');
    const [items, setItems]                   = useState<MigrationItem[]>([]);
    const [logs, setLogs]                     = useState<DryRunLog[]>([]);
    const [globalError, setGlobalError]       = useState<string | null>(null);
    const [exportError, setExportError]       = useState<string | null>(null);
    const [exportSuccess, setExportSuccess]   = useState<string | null>(null);
    const [isExporting, setIsExporting]       = useState<boolean>(false);

    // Pause/resume mechanism: when the runner detects removed fields it stores
    // a resolver here and awaits it. The Continue/Stop buttons resolve it.
    const pauseResolverRef = useRef<((continueRun: boolean) => void) | null>(null);
    const [pausedItemKey, setPausedItemKey] = useState<string | null>(null);

    // -----------------------------------------------------------------------
    // File upload
    // -----------------------------------------------------------------------

    const handleFilesSelected = useCallback((files: File[]) => {
        const file = files[0];
        if (!file) return;

        setUploadError(null);
        setUploadedTemplate(null);

        const lowerName = file.name.toLowerCase();
        const sourceType: UploadSourceType = lowerName.endsWith('.csv') || file.type === 'text/csv' ? 'csv' : 'json';

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const raw = e.target?.result?.toString() ?? '';

                if (sourceType === 'csv') {
                    const csvArray = csvTextToArray(raw);
                    const { sections, errors } = convertCSVToTemplateSections(csvArray);
                    if (errors.length > 0) {
                        setUploadError(errors.join(' '));
                        return;
                    }

                    setUploadedTemplate({
                        sourceType,
                        displayName: file.name,
                        templateForm: sectionsToTemplateForm(sections),
                        csvSections: sections,
                    });
                    return;
                }

                const parsed = JSON.parse(raw) as TemplateConfigurationModel;
                if (!parsed.templateForm?.templateFormSections) {
                    setUploadError('Invalid template configuration: missing templateForm.templateFormSections');
                    return;
                }

                setUploadedTemplate({
                    sourceType,
                    displayName: parsed.name || file.name,
                    templateForm: parsed.templateForm,
                });
            } catch {
                setUploadError(sourceType === 'csv'
                    ? 'Could not parse CSV. Please upload a valid Export Form CSV file.'
                    : 'Could not parse JSON. Please upload a valid TemplateConfiguration export.');
            }
        };
        reader.readAsText(file);
    }, []);

    // -----------------------------------------------------------------------
    // Item state helpers
    // -----------------------------------------------------------------------

    const setItemStatus = useCallback((key: string, status: ItemStatus, extra?: { removedFields?: string[]; newFields?: string[]; error?: string }) => {
        setItems(prev => prev.map(item =>
            item.key === key
                ? { ...item, status, removedFields: extra?.removedFields, newFields: extra?.newFields, error: extra?.error }
                : item
        ));
    }, []);

    const addLog = useCallback((itemKey: string, level: LogLevel, message: string) => {
        setLogs(prev => [...prev, { itemKey, level, message }]);
    }, []);

    // -----------------------------------------------------------------------
    // Pause/resume
    // -----------------------------------------------------------------------

    const waitForUserDecision = (itemKey: string): Promise<boolean> => {
        return new Promise<boolean>(resolve => {
            pauseResolverRef.current = resolve;
            setPausedItemKey(itemKey);
            setMigrationStatus('paused');
        });
    };

    const handleContinue = () => {
        pauseResolverRef.current?.(true);
        pauseResolverRef.current = null;
        setPausedItemKey(null);
        setMigrationStatus('running');
    };

    const handleStop = () => {
        pauseResolverRef.current?.(false);
        pauseResolverRef.current = null;
        setPausedItemKey(null);
        setMigrationStatus('done');
    };

    // -----------------------------------------------------------------------
    // Migration runner
    // -----------------------------------------------------------------------

    const runMigration = useCallback(async () => {
        if (!uploadedTemplate) return;

        setGlobalError(null);
        setLogs([]);
        setMigrationStatus('running');

        // Build initial item list: core module + placeholder for orgs (will expand)
        const coreItem: MigrationItem = { key: '__core__', label: 'Core module', status: 'pending' };
        setItems([coreItem]);

        try {
            const coreModuleId = getModuleId(assistantType);
            if (!coreModuleId) {
                setGlobalError(`No module ID configured for assistant type "${assistantType}" in this environment.`);
                setMigrationStatus('error');
                return;
            }

            // ------------------------------------------------------------------
            // 1. Fetch all orgs (we need at least one org to call getAvailableTemplateConfigurations)
            // ------------------------------------------------------------------
            const orgsResult = await voicifyApi.getOrganizations();
            if (orgsResult.resultType !== 'Ok' || !orgsResult.data?.length) {
                setGlobalError('Failed to fetch organizations: ' + (orgsResult.errors?.join(', ') ?? 'unknown error'));
                setMigrationStatus('error');
                return;
            }

            const orgs: OrganizationModel[] = orgsResult.data;

            // Populate org rows now that we know the list
            const orgItems: MigrationItem[] = orgs.map(org => ({
                key: org.id,
                label: org.name,
                status: 'pending' as ItemStatus,
            }));
            setItems([coreItem, ...orgItems]);

            // ------------------------------------------------------------------
            // 2. Fetch the current core module via the first org's available configs
            // ------------------------------------------------------------------
            setItemStatus('__core__', 'running');

            const availableResult = await voicifyApi.getAvailableTemplateConfigurations(orgs[0].id);
            if (availableResult.resultType !== 'Ok') {
                setItemStatus('__core__', 'error', { error: 'Failed to fetch available template configurations: ' + (availableResult.errors?.join(', ') ?? 'unknown') });
                setMigrationStatus('error');
                return;
            }

            const coreModule = availableResult.data?.find(c => c.id === coreModuleId);
            if (!coreModule) {
                setItemStatus('__core__', 'error', { error: `Core module ID ${coreModuleId} not found in available template configurations.` });
                setMigrationStatus('error');
                return;
            }

            // ------------------------------------------------------------------
            // 3. Merge new layout into the core module
            // ------------------------------------------------------------------
            if (!coreModule.templateForm) {
                setItemStatus('__core__', 'error', { error: 'Core module has no templateForm — cannot merge.' });
                setMigrationStatus('error');
                return;
            }

            const coreSourceErrors = validateSourceTemplateConfiguration(coreModule);
            if (coreSourceErrors.length > 0) {
                setItemStatus('__core__', 'error', { error: coreSourceErrors.join(' ') });
                setGlobalError(coreSourceErrors.join(' '));
                setMigrationStatus('error');
                return;
            }

            let strictCsvPlan: StrictCsvSyncPlan | undefined;

            if (uploadedTemplate.sourceType === 'csv') {
                const strictPlanResult = buildStrictCsvSyncPlan(uploadedTemplate.csvSections ?? [], coreModule);
                if (strictPlanResult.errors.length > 0 || !strictPlanResult.plan) {
                    setItemStatus('__core__', 'error', { error: strictPlanResult.errors.join(' ') });
                    setMigrationStatus('error');
                    return;
                }

                strictCsvPlan = strictPlanResult.plan;
            } else {
                let mergedCoreModule: TemplateConfigurationModel;
                let coreRemovedFields: TemplateFormFieldModel[] = [];
                const { merged: mergedCoreForm, removedFields: coreRemovedFieldsTmp } = mergeTemplateForm(
                    coreModule.templateForm,
                    uploadedTemplate.templateForm,
                );

                mergedCoreModule = {
                    ...coreModule,
                    templateForm: mergedCoreForm,
                };
                coreRemovedFields = coreRemovedFieldsTmp;

                // Log field-level changes for the core module
                logMergeChanges('__core__', coreModule.templateForm, uploadedTemplate.templateForm, mergedCoreForm, addLog);

                mergedCoreModule = normalizeTemplateConfigurationPayload(mergedCoreModule, coreModule);

                const corePayloadErrors = validateTemplateConfigurationPayload(mergedCoreModule);
                if (corePayloadErrors.length > 0) {
                    setItemStatus('__core__', 'error', { error: corePayloadErrors.join(' ') });
                    setGlobalError(corePayloadErrors.join(' '));
                    setMigrationStatus('error');
                    return;
                }

                if (coreRemovedFields.length > 0) {
                    const removedTitles = coreRemovedFields.map(f => f.title ?? f.associatedVariable ?? f.id);
                    setItemStatus('__core__', 'removed_fields', { removedFields: removedTitles });
                    const shouldContinue = await waitForUserDecision('__core__');
                    if (!shouldContinue) return;
                }

                if (isDryRun) {
                    addLog('__core__', 'info', '[DRY RUN] PUT /TemplateConfiguration skipped — no changes saved.');
                    setItemStatus('__core__', 'updated');
                } else {
                    const coreUpdateResult = await voicifyApi.updateTemplateConfiguration(coreModuleId, mergedCoreModule);
                    if (coreUpdateResult.resultType !== 'Ok') {
                        setItemStatus('__core__', 'error', { error: 'PUT failed: ' + (coreUpdateResult.errors?.join(', ') ?? 'unknown') });
                        setMigrationStatus('error');
                        return;
                    }
                    setItemStatus('__core__', 'updated');
                }
            }

            if (uploadedTemplate.sourceType === 'csv' && strictCsvPlan) {
                const strictCoreSyncResult = await runStrictCsvSyncForTemplate({
                    itemKey: '__core__',
                    template: coreModule,
                    plan: strictCsvPlan,
                    allowNewFields: true,
                    isDryRun,
                    addLog,
                    confirmNewFields: async newFields => {
                        setItemStatus('__core__', 'new_fields', { newFields: newFields.map(formatNewFieldLabel) });
                        return waitForUserDecision('__core__');
                    },
                });

                if (!strictCoreSyncResult.ok) {
                    const wasStoppedForNewFields = strictCoreSyncResult.error === 'Migration stopped because new core fields were not confirmed.';
                    if (wasStoppedForNewFields) {
                        setItemStatus('__core__', 'skipped');
                        setMigrationStatus('done');
                        return;
                    }

                    setItemStatus('__core__', 'error', { error: strictCoreSyncResult.error });
                    setMigrationStatus('error');
                    return;
                }

                setItemStatus('__core__', 'updated');
            }

            // ------------------------------------------------------------------
            // 4. Update each org's Shared Topics Module config
            // ------------------------------------------------------------------
            const customModuleName  = getCustomModuleName(assistantType);

            for (const org of orgs) {
                setItemStatus(org.id, 'running');

                try {
                    const orgConfigsResult = await voicifyApi.getOrganizationTemplateConfigurations(org.id);
                    if (orgConfigsResult.resultType !== 'Ok') {
                        setItemStatus(org.id, 'error', { error: 'Failed to fetch org configs: ' + (orgConfigsResult.errors?.join(', ') ?? 'unknown') });
                        continue;
                    }

                    // Find the org's Shared Topics Module config — same fallback chain as ManageSparkLocationsForm.
                    // When only one config exists, use it directly (matches ManageSparkLocationsForm behavior).
                    const orgConfigs = orgConfigsResult.data ?? [];
                    let orgConfig: TemplateConfigurationModel | null = null;
                    if (orgConfigs.length === 1) {
                        orgConfig = orgConfigs[0];
                    } else if (orgConfigs.length > 1) {
                        orgConfig =
                            orgConfigs.find(c => c.name === customModuleName) ??
                            orgConfigs.find(c => c.name === aiAnsweringCustomModuleName || c.name === aiDentalSchedulingCustomModuleName) ??
                            orgConfigs[0]; // final fallback: first available (matches ManageSparkLocationsForm)
                    }

                    if (!orgConfig || !orgConfig.templateForm) {
                        setItemStatus(org.id, 'skipped');
                        continue;
                    }

                    const orgSourceErrors = validateSourceTemplateConfiguration(orgConfig);
                    if (orgSourceErrors.length > 0) {
                        setItemStatus(org.id, 'error', { error: orgSourceErrors.join(' ') });
                        continue;
                    }

                    // Verify the org's Shared Topics app actually has the core module applied.
                    // orgConfig.templateApplicationId is the Shared Topics common app for this org.
                    // We check that app's applied templates for one whose templateForm.templateConfigurationId
                    // matches the core module GUID — if none found, this org is on a different module
                    // and should not be touched.
                    if (orgConfig.templateApplicationId) {
                        const appliedResult = await voicifyApi.getAppliedApplicationTemplatesForApp(orgConfig.templateApplicationId);
                        if (appliedResult.resultType !== 'Ok') {
                            setItemStatus(org.id, 'error', { error: 'Failed to fetch applied templates for Shared Topics app: ' + (appliedResult.errors?.join(', ') ?? 'unknown') });
                            continue;
                        }

                        const hasCoreModule = appliedResult.data?.some(
                            at => at.templateForm?.templateConfigurationId === coreModuleId
                        );

                        if (!hasCoreModule) {
                            setItemStatus(org.id, 'skipped');
                            continue;
                        }
                    } else {
                        // No templateApplicationId on the org config — can't verify, skip to be safe
                        setItemStatus(org.id, 'skipped');
                        continue;
                    }

                    if (uploadedTemplate.sourceType === 'csv') {
                        if (!strictCsvPlan) {
                            setItemStatus(org.id, 'error', { error: 'Strict CSV sync plan was not available.' });
                            continue;
                        }

                        const strictOrgSyncResult = await runStrictCsvSyncForTemplate({
                            itemKey: org.id,
                            template: orgConfig,
                            plan: strictCsvPlan,
                            allowNewFields: false,
                            isDryRun,
                            addLog,
                        });

                        if (!strictOrgSyncResult.ok) {
                            setItemStatus(org.id, 'error', { error: strictOrgSyncResult.error });
                            continue;
                        }

                        setItemStatus(org.id, 'updated');
                    } else {
                        let mergedOrgConfig: TemplateConfigurationModel;
                        let orgRemovedFields: TemplateFormFieldModel[] = [];
                        const { merged: mergedOrgForm, removedFields: orgRemovedFieldsTmp } = mergeTemplateForm(
                            orgConfig.templateForm,
                            uploadedTemplate.templateForm,
                        );

                        // Log field-level changes for this org
                        logMergeChanges(org.id, orgConfig.templateForm, uploadedTemplate.templateForm, mergedOrgForm, addLog);

                        mergedOrgConfig = {
                            ...orgConfig,
                            templateForm: mergedOrgForm,
                        };
                        orgRemovedFields = orgRemovedFieldsTmp;

                        mergedOrgConfig = normalizeTemplateConfigurationPayload(mergedOrgConfig, orgConfig);

                        const orgPayloadErrors = validateTemplateConfigurationPayload(mergedOrgConfig);
                        if (orgPayloadErrors.length > 0) {
                            setItemStatus(org.id, 'error', { error: orgPayloadErrors.join(' ') });
                            setGlobalError(orgPayloadErrors.join(' '));
                            setMigrationStatus('error');
                            return;
                        }

                        if (orgRemovedFields.length > 0) {
                            const removedTitles = orgRemovedFields.map(f => f.title ?? f.associatedVariable ?? f.id);
                            setItemStatus(org.id, 'removed_fields', { removedFields: removedTitles });
                            const shouldContinue = await waitForUserDecision(org.id);
                            if (!shouldContinue) return;
                        }

                        if (isDryRun) {
                            addLog(org.id, 'info', '[DRY RUN] PUT /TemplateConfiguration skipped — no changes saved.');
                            setItemStatus(org.id, 'updated');
                        } else {
                            const orgUpdateResult = await voicifyApi.updateTemplateConfiguration(orgConfig.id, mergedOrgConfig);
                            if (orgUpdateResult.resultType !== 'Ok') {
                                setItemStatus(org.id, 'error', { error: 'PUT failed: ' + (orgUpdateResult.errors?.join(', ') ?? 'unknown') });
                                continue;
                            }
                            setItemStatus(org.id, 'updated');
                        }
                    }

                } catch (orgErr) {
                    setItemStatus(org.id, 'error', { error: String(orgErr) });
                }
            }

            setMigrationStatus('done');

        } catch (err) {
            setGlobalError(String(err));
            setMigrationStatus('error');
        }
    }, [uploadedTemplate, assistantType, isDryRun, addLog, setItemStatus]);

    // -----------------------------------------------------------------------
    // Derived display values
    // -----------------------------------------------------------------------

    const coreModuleId   = getModuleId(assistantType);
    const envLabel       = process.env.VOICIFY_ENVIRONMENT ?? 'unknown';
    const parsedSections = uploadedTemplate?.templateForm?.templateFormSections ?? [];
    const parsedFieldCount = parsedSections.reduce((sum, s) => sum + (s.templateFormFields?.length ?? 0), 0);

    const isRunning = migrationStatus === 'running' || migrationStatus === 'paused';
    const canRun    = !!uploadedTemplate && !isRunning;
    const isDone    = migrationStatus === 'done' || migrationStatus === 'error';

    const handleReset = useCallback(() => {
        setMigrationStatus('idle');
        setItems([]);
        setLogs([]);
        setGlobalError(null);
        setPausedItemKey(null);
        pauseResolverRef.current = null;
    }, []);

    const handleExportAllModules = useCallback(async () => {
        setExportError(null);
        setExportSuccess(null);
        setIsExporting(true);

        try {
            const orgsResult = await voicifyApi.getOrganizations();
            if (orgsResult.resultType !== 'Ok' || !orgsResult.data?.length) {
                setExportError('Failed to fetch organizations: ' + (orgsResult.errors?.join(', ') ?? 'unknown error'));
                return;
            }

            const orgs: OrganizationModel[] = orgsResult.data;
            const availableResult = await voicifyApi.getAvailableTemplateConfigurations(orgs[0].id);
            if (availableResult.resultType !== 'Ok') {
                setExportError('Failed to fetch available template configurations: ' + (availableResult.errors?.join(', ') ?? 'unknown error'));
                return;
            }

            const availableConfigs = availableResult.data ?? [];
            const core = exportAssistantTypes.reduce((accumulator, type) => {
                const moduleId = getModuleId(type);
                accumulator[type] = moduleId ? (availableConfigs.find(config => config.id === moduleId) ?? null) : null;
                return accumulator;
            }, {} as Record<ExportAssistantType, TemplateConfigurationModel | null>);

            const organizations: ModuleExportBundle['organizations'] = [];

            for (const org of orgs) {
                const orgConfigsResult = await voicifyApi.getOrganizationTemplateConfigurations(org.id);
                if (orgConfigsResult.resultType !== 'Ok') {
                    setExportError(`Failed to fetch module configs for ${org.name}: ${orgConfigsResult.errors?.join(', ') ?? 'unknown error'}`);
                    return;
                }

                const orgConfigs = orgConfigsResult.data ?? [];
                organizations.push({
                    organizationId: org.id,
                    organizationName: org.name,
                    modules: {
                        'ai-ordering': getOrgSpecificModuleConfig(orgConfigs, 'ai-ordering'),
                        'dental-scheduling': getOrgSpecificModuleConfig(orgConfigs, 'dental-scheduling'),
                    }
                });
            }

            const bundle: ModuleExportBundle = {
                exportedAt: new Date().toISOString(),
                environment: process.env.VOICIFY_ENVIRONMENT ?? 'unknown',
                core,
                organizations,
            };

            downloadJsonFile(`module-export-ordering-and-dental-${bundle.environment}-${Date.now()}.json`, bundle);
            setExportSuccess(`Exported ${organizations.length} organization module bundles plus core ordering and dental modules.`);
        } finally {
            setIsExporting(false);
        }
    }, []);

    const handleExportAppliedModulesCsv = useCallback(async () => {
        setExportError(null);
        setExportSuccess(null);
        setIsExporting(true);

        try {
            const orgsResult = await voicifyApi.getOrganizations();
            if (orgsResult.resultType !== 'Ok' || !orgsResult.data?.length) {
                setExportError('Failed to fetch organizations: ' + (orgsResult.errors?.join(', ') ?? 'unknown error'));
                return;
            }

            const rows: Array<Record<string, string | number>> = [];
            let exportedModuleCount = 0;
            const appRequestConcurrency = 5;

            for (const org of orgsResult.data) {
                const orgConfigsResult = await voicifyApi.getOrganizationTemplateConfigurations(org.id);
                if (orgConfigsResult.resultType !== 'Ok') {
                    setExportError(`Failed to fetch module configs for ${org.name}: ${orgConfigsResult.errors?.join(', ') ?? 'unknown error'}`);
                    return;
                }

                const applicationsResult = await voicifyApi.getApplicationsForOrg(org.id);
                if (applicationsResult.resultType !== 'Ok') {
                    setExportError(`Failed to fetch applications for ${org.name}: ${applicationsResult.errors?.join(', ') ?? 'unknown error'}`);
                    return;
                }

                const orgSpecificModules = exportAssistantTypes.reduce((accumulator, assistantType) => {
                    const orgSpecificConfig = getOrgSpecificModuleConfig(orgConfigsResult.data ?? [], assistantType);
                    if (orgSpecificConfig?.id) {
                        accumulator[orgSpecificConfig.id] = {
                            assistantType,
                            templateConfigurationId: orgSpecificConfig.id,
                            templateConfigurationName: orgSpecificConfig.name ?? '',
                            templateApplicationId: orgSpecificConfig.templateApplicationId ?? '',
                            templateSource: 'org' as TemplateSourceType,
                        };
                    }
                    return accumulator;
                }, {} as Record<string, AppliedModuleExportMatch>);

                const coreModules = exportAssistantTypes.reduce((accumulator, assistantType) => {
                    const moduleId = getModuleId(assistantType);
                    if (moduleId) {
                        accumulator[moduleId] = {
                            assistantType,
                            templateConfigurationId: moduleId,
                            templateConfigurationName: '',
                            templateApplicationId: '',
                            templateSource: 'core' as TemplateSourceType,
                        };
                    }
                    return accumulator;
                }, {} as Record<string, AppliedModuleExportMatch>);

                const targetModulesById = {
                    ...coreModules,
                    ...orgSpecificModules,
                };

                const applications = applicationsResult.data ?? [];
                const manageMenuAppIds = new Set(
                    applications
                        .map(application => application.manageMenuApplicationId)
                        .filter((id): id is string => !!id)
                );

                const candidateApplications = applications.filter(application => {
                    if (!application.id) {
                        return false;
                    }

                    if (isManageMenuApp(application, manageMenuAppIds)) {
                        return false;
                    }

                    return true;
                });

                const applicationExports = await mapWithConcurrency(candidateApplications, appRequestConcurrency, async application => {
                    const appliedTemplatesResult = await voicifyApi.getAppliedApplicationTemplatesForApp(application.id);
                    if (appliedTemplatesResult.resultType !== 'Ok') {
                        throw new Error(`Failed to fetch applied modules for ${org.name} / ${application.name ?? application.id}: ${appliedTemplatesResult.errors?.join(', ') ?? 'unknown error'}`);
                    }

                    const applicationType: ExportApplicationType = application.allowsTemplating ? 'common' : 'location';
                    const matchedRows: Array<Record<string, string | number>> = [];
                    let matchedModuleCount = 0;

                    (appliedTemplatesResult.data ?? []).forEach(appliedTemplate => {
                        const templateConfigurationId = appliedTemplate.templateForm?.templateConfigurationId;
                        if (!templateConfigurationId) {
                            return;
                        }

                        const matchedModule = targetModulesById[templateConfigurationId];
                        if (!matchedModule) {
                            return;
                        }

                        const resolvedMatchedModule: AppliedModuleExportMatch = {
                            ...matchedModule,
                            templateConfigurationName: matchedModule.templateConfigurationName || appliedTemplate.templateForm?.templateConfiguration?.name || appliedTemplate.name || '',
                            templateApplicationId: matchedModule.templateApplicationId || appliedTemplate.templateForm?.templateConfiguration?.templateApplicationId || '',
                        };

                        matchedModuleCount += 1;
                        matchedRows.push(...buildAppliedModuleCsvRows(org, application, applicationType, resolvedMatchedModule, appliedTemplate));
                    });

                    return {
                        matchedRows,
                        matchedModuleCount,
                    };
                });

                applicationExports.forEach(applicationExport => {
                    exportedModuleCount += applicationExport.matchedModuleCount;
                    rows.push(...applicationExport.matchedRows);
                });
            }

            exportCSV(rows, APPLIED_MODULE_CSV_HEADERS, `applied-modules-ordering-and-dental-${process.env.VOICIFY_ENVIRONMENT ?? 'unknown'}-${Date.now()}`);
            setExportSuccess(`Exported ${rows.length} rows across ${exportedModuleCount} applied ordering and dental modules.`);
        } catch (error) {
            setExportError(error instanceof Error ? error.message : String(error));
        } finally {
            setIsExporting(false);
        }
    }, []);

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------

    return (
        <div className={containerStyle}>
            <h1 className={headingStyle}>Module Migration Tool</h1>
            <p className={subheadingStyle}>
                Updates the core platform module and all org-level Shared Topics Module configs with a new field layout.
                CSV imports now match fields by <code>associatedVariable</code> and only move or update existing fields; JSON keeps the existing layout merge rules.
            </p>

            <SettingsCard title="Export modules">
                <div className={sectionBodyStyle}>
                    <p className={helperTextStyle}>
                        Export the core AI Ordering and Dental Scheduling modules, plus every organization-specific module and field for those assistant types.
                    </p>
                    <div className={buttonRowStyle}>
                        <button
                            className={runButtonStyle}
                            onClick={handleExportAllModules}
                            disabled={isExporting || isRunning}
                        >
                            {isExporting ? 'Exporting…' : 'Export all ordering + dental modules'}
                        </button>
                        <button
                            className={resetButtonStyle}
                            onClick={handleExportAppliedModulesCsv}
                            disabled={isExporting || isRunning}
                        >
                            {isExporting ? 'Exporting…' : 'Export applied modules CSV'}
                        </button>
                    </div>
                    {exportError && <p className={errorTextStyle}>{exportError}</p>}
                    {exportSuccess && <p className={doneTextStyle}>{exportSuccess}</p>}
                </div>
            </SettingsCard>

            {/* ---- Step 1: Upload ---- */}
            <SettingsCard title="1. Upload new template config">
                <div className={sectionBodyStyle}>
                    <MultiFileSelector
                        accept=".json,.csv,application/json,text/csv"
                        uploadLabel="Drop exported TemplateConfiguration JSON or Export Form CSV here"
                        uploadButtonText="Select JSON or CSV file"
                        onSelected={handleFilesSelected}
                        maxSizeOverrideMegabytes={10}
                    />
                    {uploadError && (
                        <p className={errorTextStyle}>{uploadError}</p>
                    )}
                    {uploadedTemplate && !uploadError && (
                        <div className={parsePreviewStyle}>
                            <span className={successTextStyle}>Parsed successfully:</span>
                            {' '}&ldquo;{uploadedTemplate.displayName}&rdquo;
                            {' — '}{parsedSections.length} section{parsedSections.length !== 1 ? 's' : ''},{' '}
                            {parsedFieldCount} field{parsedFieldCount !== 1 ? 's' : ''}
                        </div>
                    )}
                </div>
            </SettingsCard>

            {/* ---- Step 2: Configure ---- */}
            <SettingsCard title="2. Configure">
                <div className={sectionBodyStyle}>
                    <label className={labelStyle}>Assistant type</label>
                    <select
                        className={selectStyle}
                        value={assistantType}
                        onChange={e => setAssistantType(e.target.value as AssistantType)}
                        disabled={isRunning}
                    >
                        <option value="ai-answering">AI Answering</option>
                        <option value="ai-ordering">AI Ordering</option>
                        <option value="dental-scheduling">AI Dental Scheduling</option>
                    </select>

                    <div className={metaRowStyle}>
                        <span className={metaLabelStyle}>Core module ID:</span>
                        <code className={codeStyle}>{coreModuleId ?? 'not configured for this environment'}</code>
                    </div>
                    <div className={metaRowStyle}>
                        <span className={metaLabelStyle}>Environment:</span>
                        <code className={codeStyle}>{envLabel}</code>
                    </div>
                    <div className={metaRowStyle}>
                        <span className={metaLabelStyle}>Org-level config name:</span>
                        <code className={codeStyle}>{getCustomModuleName(assistantType)}</code>
                    </div>
                    <div className={metaRowStyle}>
                        <span className={metaLabelStyle}>Common app name:</span>
                        <code className={codeStyle}>{getCommonAppName(assistantType)}</code>
                    </div>

                    <div className={dryRunToggleRowStyle}>
                        <label className={dryRunLabelStyle}>
                            <input
                                type="checkbox"
                                checked={isDryRun}
                                onChange={e => setIsDryRun(e.target.checked)}
                                disabled={isRunning}
                            />
                            {' '}Dry run
                        </label>
                        <span className={dryRunHintStyle}>
                            {isDryRun
                                ? 'No changes will be saved. Logs show exactly what would be updated.'
                                : 'Live mode — changes will be written to the API.'}
                        </span>
                    </div>
                </div>
            </SettingsCard>

            {/* ---- Step 3: Run ---- */}
            <SettingsCard title="3. Run migration">
                <div className={sectionBodyStyle}>
                    <div className={buttonRowStyle}>
                        <button
                            className={isDryRun ? dryRunButtonStyle : runButtonStyle}
                            onClick={runMigration}
                            disabled={!canRun || isRunning}
                        >
                            {isDryRun ? 'Run Dry Run' : 'Run Migration'}
                        </button>
                        {isDone && (
                            <button
                                className={resetButtonStyle}
                                onClick={handleReset}
                            >
                                Reset
                            </button>
                        )}
                    </div>

                    {globalError && (
                        <p className={errorTextStyle}>{globalError}</p>
                    )}

                    {items.length > 0 && (
                        <div className={resultsStyle}>
                            {items.map(item => (
                                <div key={item.key} className={resultRowStyle}>
                                    <span className={statusIconStyle(item.status)}>
                                        {statusIcon(item.status)}
                                    </span>
                                    <div className={resultBodyStyle}>
                                        <span className={itemLabelStyle}>{item.label}</span>
                                        {item.status === 'error' && item.error && (
                                            <span className={errorDetailStyle}>{item.error}</span>
                                        )}
                                        {item.status === 'removed_fields' && item.removedFields && (
                                            <div className={removedFieldsStyle}>
                                                <span className={removedLabelStyle}>
                                                    Removed fields detected — migration paused:
                                                </span>
                                                <ul className={removedListStyle}>
                                                    {item.removedFields.map((f, i) => (
                                                        <li key={i}>{f}</li>
                                                    ))}
                                                </ul>
                                                {migrationStatus === 'paused' && pausedItemKey === item.key && (
                                                    <div className={pauseActionsStyle}>
                                                        <button
                                                            className={continueButtonStyle}
                                                            onClick={handleContinue}
                                                        >
                                                            Continue anyway
                                                        </button>
                                                        <button
                                                            className={stopButtonStyle}
                                                            onClick={handleStop}
                                                        >
                                                            Stop migration
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {item.status === 'new_fields' && item.newFields && (
                                            <div className={removedFieldsStyle}>
                                                <span className={removedLabelStyle}>
                                                    New core fields detected — migration paused:
                                                </span>
                                                <ul className={removedListStyle}>
                                                    {item.newFields.map((f, i) => (
                                                        <li key={i}>{f}</li>
                                                    ))}
                                                </ul>
                                                {migrationStatus === 'paused' && pausedItemKey === item.key && (
                                                    <div className={pauseActionsStyle}>
                                                        <button
                                                            className={continueButtonStyle}
                                                            onClick={handleContinue}
                                                        >
                                                            Add new core fields
                                                        </button>
                                                        <button
                                                            className={stopButtonStyle}
                                                            onClick={handleStop}
                                                        >
                                                            Stop migration
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {migrationStatus === 'done' && (
                        <p className={doneTextStyle}>
                            {isDryRun ? 'Dry run finished' : 'Migration finished'}.{' '}
                            {items.filter(i => i.status === 'updated').length} item(s) {isDryRun ? 'would be updated' : 'updated'},{' '}
                            {items.filter(i => i.status === 'skipped').length} skipped,{' '}
                            {items.filter(i => i.status === 'error').length} error(s).
                        </p>
                    )}

                    {logs.length > 0 && (
                        <div className={logPanelStyle}>
                            <div className={logHeaderStyle}>
                                Change log {isDryRun && <span className={dryRunBadgeStyle}>DRY RUN</span>}
                            </div>
                            <div className={logBodyStyle}>
                                {logs.map((log, i) => (
                                    <div key={i} className={logLineStyle(log.level)}>
                                        <span className={logIconStyle}>{logIcon(log.level)}</span>
                                        <span className={logItemKeyStyle}>{log.itemKey === '__core__' ? 'core' : log.itemKey.slice(0, 8)}</span>
                                        <span>{log.message}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </SettingsCard>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Status icon helper
// ---------------------------------------------------------------------------

function statusIcon(status: ItemStatus): string {
    switch (status) {
        case 'pending':        return '○';
        case 'running':        return '⏳';
        case 'updated':        return '✓';
        case 'skipped':        return '–';
        case 'error':          return '✗';
        case 'removed_fields': return '⚠';
        case 'new_fields':     return '＋';
    }
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const containerStyle = css`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const headingStyle = css`
    font-family: Muli;
    font-size: 24px;
    font-weight: 700;
    color: ${color_shades_darkest};
    margin: 0 0 4px 0;
`;

const subheadingStyle = css`
    font-family: Muli;
    font-size: 14px;
    color: ${color_shades_darker};
    margin: 0 0 8px 0;
    line-height: 1.5;

    code {
        background: ${color_shades_lighter};
        padding: 1px 4px;
        border-radius: 3px;
        font-size: 13px;
    }
`;

const sectionBodyStyle = css`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
`;

const labelStyle = css`
    font-family: Muli;
    font-size: 13px;
    font-weight: 600;
    color: ${color_shades_darkest};
`;

const selectStyle = css`
    font-family: Muli;
    font-size: 14px;
    color: ${color_shades_darkest};
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_light};
    border-radius: 4px;
    padding: 6px 10px;
    max-width: 260px;
    cursor: pointer;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const metaRowStyle = css`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const metaLabelStyle = css`
    font-family: Muli;
    font-size: 13px;
    color: ${color_shades_darker};
    min-width: 160px;
`;

const codeStyle = css`
    font-size: 12px;
    background: ${color_shades_lighter};
    color: ${color_shades_darkest};
    padding: 2px 6px;
    border-radius: 3px;
`;

const parsePreviewStyle = css`
    font-family: Muli;
    font-size: 13px;
    color: ${color_shades_darkest};
    background: ${color_shades_lighter};
    border-radius: 4px;
    padding: 8px 12px;
`;

const successTextStyle = css`
    font-weight: 600;
    color: ${greeny_blue};
`;

const errorTextStyle = css`
    font-family: Muli;
    font-size: 13px;
    color: ${faded_red};
`;

const helperTextStyle = css`
    font-family: Muli;
    font-size: 13px;
    color: ${color_shades_darker};
    margin: 0;
    line-height: 1.5;
`;

const runButtonStyle = css`
    font-family: Muli;
    font-size: 14px;
    font-weight: 600;
    color: ${color_defaults_white};
    background: ${dark_sky_blue};
    border: none;
    border-radius: 4px;
    padding: 10px 20px;
    cursor: pointer;
    align-self: flex-start;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const resultsStyle = css`
    display: flex;
    flex-direction: column;
    gap: 2px;
    border: 1px solid ${color_shades_light};
    border-radius: 4px;
    overflow: hidden;
    margin-top: 8px;
`;

const buttonRowStyle = css`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const resetButtonStyle = css`
    font-family: Muli;
    font-size: 14px;
    font-weight: 600;
    color: ${color_shades_darkest};
    background: ${color_shades_light};
    border: none;
    border-radius: 4px;
    padding: 10px 20px;
    cursor: pointer;
`;

const resultRowStyle = css`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 8px 12px;
    background: ${color_defaults_white};
    border-bottom: 1px solid ${color_shades_light};
    font-family: Muli;
    font-size: 13px;

    &:last-child {
        border-bottom: none;
    }
`;

const resultBodyStyle = css`
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
`;

const itemLabelStyle = css`
    font-family: Muli;
    font-size: 13px;
    color: ${color_shades_darkest};
`;

const errorDetailStyle = css`
    font-family: Muli;
    font-size: 12px;
    color: ${faded_red};
`;

const statusIconStyle = (status: ItemStatus) => css`
    font-size: 14px;
    min-width: 18px;
    color: ${
        status === 'updated'        ? greeny_blue  :
        status === 'error'          ? faded_red    :
        status === 'removed_fields' ? '#c87800'    :
        status === 'new_fields'     ? dark_sky_blue:
        status === 'skipped'        ? cool_grey    :
        color_shades_darker
    };
`;

const removedFieldsStyle = css`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const removedLabelStyle = css`
    font-family: Muli;
    font-size: 12px;
    font-weight: 600;
    color: #c87800;
`;

const removedListStyle = css`
    margin: 0;
    padding-left: 20px;
    font-family: Muli;
    font-size: 12px;
    color: ${color_shades_darkest};

    li {
        margin-bottom: 2px;
    }
`;

const pauseActionsStyle = css`
    display: flex;
    gap: 8px;
    margin-top: 4px;
`;

const continueButtonStyle = css`
    font-family: Muli;
    font-size: 12px;
    font-weight: 600;
    color: ${color_defaults_white};
    background: #c87800;
    border: none;
    border-radius: 4px;
    padding: 6px 14px;
    cursor: pointer;
`;

const stopButtonStyle = css`
    font-family: Muli;
    font-size: 12px;
    font-weight: 600;
    color: ${color_shades_darkest};
    background: ${color_shades_light};
    border: none;
    border-radius: 4px;
    padding: 6px 14px;
    cursor: pointer;
`;

const doneTextStyle = css`
    font-family: Muli;
    font-size: 13px;
    color: ${color_shades_darkest};
    margin-top: 8px;
`;

const dryRunToggleRowStyle = css`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 4px;
`;

const dryRunLabelStyle = css`
    font-family: Muli;
    font-size: 14px;
    font-weight: 600;
    color: ${color_shades_darkest};
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
`;

const dryRunHintStyle = css`
    font-family: Muli;
    font-size: 12px;
    color: ${color_shades_darker};
`;

const dryRunButtonStyle = css`
    font-family: Muli;
    font-size: 14px;
    font-weight: 600;
    color: ${color_defaults_white};
    background: #c87800;
    border: none;
    border-radius: 4px;
    padding: 10px 20px;
    cursor: pointer;
    align-self: flex-start;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const dryRunBadgeStyle = css`
    font-family: Muli;
    font-size: 11px;
    font-weight: 700;
    color: ${color_defaults_white};
    background: #c87800;
    border-radius: 3px;
    padding: 2px 6px;
    margin-left: 8px;
    letter-spacing: 0.04em;
    vertical-align: middle;
`;

const logPanelStyle = css`
    border: 1px solid ${color_shades_light};
    border-radius: 4px;
    overflow: hidden;
    margin-top: 12px;
    font-family: monospace;
`;

const logHeaderStyle = css`
    background: ${color_shades_lighter};
    padding: 8px 12px;
    font-family: Muli;
    font-size: 13px;
    font-weight: 600;
    color: ${color_shades_darkest};
    border-bottom: 1px solid ${color_shades_light};
`;

const logBodyStyle = css`
    max-height: 400px;
    overflow-y: auto;
    background: ${color_defaults_white};
`;

const logLineStyle = (level: LogLevel) => css`
    display: flex;
    align-items: baseline;
    gap: 8px;
    padding: 3px 12px;
    font-size: 12px;
    line-height: 1.6;
    color: ${
        level === 'add'    ? '#1a7a3e' :
        level === 'remove' ? faded_red :
        level === 'change' ? '#1a5799' :
        level === 'warn'   ? '#c87800' :
        level === 'skip'   ? cool_grey :
        color_shades_darkest
    };
    background: ${
        level === 'add'    ? '#f0fdf4' :
        level === 'remove' ? '#fff5f5' :
        level === 'change' ? '#f0f6ff' :
        level === 'warn'   ? '#fffbeb' :
        'transparent'
    };
    border-bottom: 1px solid ${color_shades_light};

    &:last-child {
        border-bottom: none;
    }
`;

const logIconStyle = css`
    min-width: 14px;
    font-size: 11px;
`;

const logItemKeyStyle = css`
    min-width: 72px;
    font-size: 11px;
    color: ${color_shades_darker};
    flex-shrink: 0;
`;

// ---------------------------------------------------------------------------
// Log icon helper
// ---------------------------------------------------------------------------

function logIcon(level: LogLevel): string {
    switch (level) {
        case 'add':    return '+';
        case 'remove': return '−';
        case 'change': return '~';
        case 'warn':   return '⚠';
        case 'skip':   return '–';
        case 'info':   return 'i';
    }
}

export default MigrationRunner;
